import { useEffect, useState } from "react";
import { IconLock } from "@tabler/icons-react";
import { EmptyState, Loader, PageHeader } from "@/components/ui";
import { useCatalogo, useRelaciones } from "../hooks/use-reporteria";
import { usePreviewInfinito } from "../hooks/use-preview";
import { emptyDef, esEjecutable, hidratarEtiquetas } from "../state/builder-ops";
import type { RepDefinicion as RepDef } from "../services/reporteria.api";
import { CatalogoTablas } from "./CatalogoTablas";
import { ColumnasSeleccionadas } from "./ColumnasSeleccionadas";
import { AjustesReporte } from "./AjustesReporte";
import { RelacionesYFormulas } from "./RelacionesYFormulas";
import { PreviewTabla } from "./PreviewTabla";
import { BuilderToolbar } from "./BuilderToolbar";

export function ReporteriaBuilderPage() {
  const catalogo = useCatalogo();
  const relaciones = useRelaciones();
  const [def, setDefState] = useState<RepDef>(() => emptyDef());
  const setDef = (f: (d: RepDef) => RepDef) => setDefState((prev) => f(prev));
  const activo = esEjecutable(def);
  const datosPreview = usePreviewInfinito(def, activo);

  // Rellena las etiquetas legibles desde el catálogo cuando falten (p. ej. al
  // abrir un predefinido/plantilla guardado) · evita mostrar tokens opacos.
  // `hidratarEtiquetas` es idempotente (mismo objeto si nada cambia) → sin bucles.
  useEffect(() => {
    if (catalogo.data) setDefState((d) => hidratarEtiquetas(d, catalogo.data!));
  }, [catalogo.data, def]);

  if (catalogo.isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader />
      </div>
    );
  }

  if (catalogo.isError || !catalogo.data) {
    return (
      <div className="p-6">
        <EmptyState
          icon={IconLock}
          title="Sin acceso a reportería"
          description="No tienes ninguna configuración de reportes asignada. Pídele a un administrador que te habilite tablas y columnas."
        />
      </div>
    );
  }

  return (
    <div className="flex h-[calc(100dvh-4rem)] flex-col gap-3 p-4">
      <PageHeader
        title="Reportería"
        description="Arma tu reporte: elige columnas, filtra, previsualiza y exporta a Excel."
      />
      <BuilderToolbar def={def} setDef={setDef} />

      <div className="grid min-h-0 flex-1 gap-3 lg:grid-cols-[18rem_minmax(0,1fr)_minmax(0,1fr)]">
        <div className="min-h-0 lg:h-full">
          <CatalogoTablas catalogo={catalogo.data} def={def} setDef={setDef} />
        </div>

        <div className="min-h-0 space-y-3 overflow-y-auto lg:h-full">
          <ColumnasSeleccionadas def={def} setDef={setDef} />
          <AjustesReporte def={def} setDef={setDef} catalogo={catalogo.data} />
          <RelacionesYFormulas
            def={def}
            setDef={setDef}
            catalogo={catalogo.data}
            relaciones={relaciones.data ?? []}
          />
        </div>

        <div className="min-h-0 lg:h-full">
          <PreviewTabla activo={activo} datos={datosPreview} def={def} setDef={setDef} />
        </div>
      </div>
    </div>
  );
}
